import { AppLayout } from '@/components/AppLayout';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bell, CheckCheck, Briefcase, FileCheck, Video,
  UserPlus, Clock, UserX, Send, ExternalLink, Users, Search
} from 'lucide-react';
import { useEmployerNotifications, type EmployerNotification } from '@/hooks/useEmployerNotifications';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatRelativeTime } from '@/lib/localizedDate';

const typeIcons: Record<string, typeof Bell> = {
  new_application: Briefcase,
  cv_scored: FileCheck,
  interview_completed: Video,
  interview_incomplete: Clock,
  candidate_offer_accepted: CheckCheck,
  offer_accepted: CheckCheck,
  offer_declined: UserX,
  offer_clarification: Send,
  candidate_withdrawn: UserX,
  team_member_joined: UserPlus,
  job_expired: Clock,
  job_expiring_soon: Clock,
  sourcing_invite_response: Send,
};

const categoryMap: Record<string, string> = {
  new_application: 'applications',
  candidate_withdrawn: 'applications',
  candidate_offer_accepted: 'applications',
  offer_accepted: 'applications',
  offer_declined: 'applications',
  offer_clarification: 'applications',
  cv_scored: 'applications',
  interview_completed: 'interviews',
  interview_incomplete: 'interviews',
  team_member_joined: 'system',
  job_expired: 'system',
  job_expiring_soon: 'system',
  sourcing_invite_response: 'applications',
};

function getPayloadString(payload: Record<string, unknown> | null, key: string) {
  const value = payload?.[key];
  return typeof value === 'string' ? value : null;
}

function getNotificationLink(n: EmployerNotification): string | null {
  const payload = n.payload_json as Record<string, unknown> | null;
  if (!payload) return null;

  const candidateId = getPayloadString(payload, 'candidate_id');
  const applicationId = getPayloadString(payload, 'application_id');
  const jobId = getPayloadString(payload, 'job_id');

  switch (n.notification_type) {
    case 'new_application':
    case 'cv_scored':
    case 'candidate_withdrawn':
    case 'candidate_offer_accepted':
    case 'offer_accepted':
    case 'offer_declined':
    case 'offer_clarification':
      if (candidateId && applicationId) return `/candidates/${candidateId}?app=${applicationId}`;
      if (candidateId) return `/candidates/${candidateId}`;
      return null;
    case 'interview_completed':
    case 'interview_incomplete':
      if (candidateId && applicationId) return `/candidates/${candidateId}?app=${applicationId}`;
      if (candidateId) return `/candidates/${candidateId}`;
      return null;
    case 'job_expired':
    case 'job_expiring_soon':
      if (jobId) return `/jobs/${jobId}`;
      return null;
    default:
      return null;
  }
}

export default function Notifications() {
  const { notifications, isLoading, unreadCount, isAdmin, markRead, markAllRead } = useEmployerNotifications();
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const [userFilter, setUserFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch team members for admin filter
  const { data: teamMembers } = useQuery({
    queryKey: ['team-members-for-filter', profile?.org_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hire_profiles')
        .select('user_id, full_name, email')
        .eq('org_id', profile!.org_id!)
        .eq('status', 'active')
        .order('full_name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profile?.org_id && isAdmin,
  });

  // Filter notifications by selected user and search query
  const filteredNotifications = useMemo(() => {
    let result = notifications;
    if (isAdmin && userFilter !== 'all') {
      result = userFilter === 'org_wide'
        ? result.filter(n => !n.target_user_id)
        : result.filter(n => n.target_user_id === userFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(n => {
        const payload = n.payload_json as Record<string, unknown> | null;
        const jobTitle = getPayloadString(payload, 'job_title') || '';
        return n.title.toLowerCase().includes(q) || n.body?.toLowerCase().includes(q) || jobTitle.toLowerCase().includes(q);
      });
    }
    return result;
  }, [notifications, userFilter, isAdmin, searchQuery]);

  const filterByCategory = (category: string) =>
    filteredNotifications.filter(n => (categoryMap[n.notification_type] || 'system') === category);

  const appCount = filterByCategory('applications').filter(n => !n.read_at).length;
  const intCount = filterByCategory('interviews').filter(n => !n.read_at).length;
  const sysCount = filterByCategory('system').filter(n => !n.read_at).length;
  const filteredUnread = filteredNotifications.filter(n => !n.read_at).length;

  return (
    <AppLayout>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className="p-5 sm:p-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground">{t('hire.notifications.title')}</h1>
              <p className="text-muted-foreground mt-1.5 text-sm">
                {filteredUnread > 0
                  ? t('hire.notifications.unreadCount', { count: filteredUnread })
                  : t('hire.notifications.allCaughtUp')}
              </p>
            </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('hire.notifications.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ps-9 w-[240px] h-10 rounded-xl"
              />
            </div>
            {/* Admin: filter by team member */}
            {isAdmin && teamMembers && teamMembers.length > 0 && (
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-[220px] h-10 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <SelectValue placeholder={t('hire.notifications.allTeamMembers')} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('hire.notifications.allTeamMembers')}</SelectItem>
                  <SelectItem value="org_wide">{t('hire.notifications.orgWideOnly')}</SelectItem>
                  {teamMembers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.full_name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => markAllRead()}>
                <CheckCheck className="w-4 h-4 me-1.5" />
                {t('hire.notifications.markAllRead')}
              </Button>
            )}
          </div>
          </div>
        </Card>

        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">
              {t('common.all')}
              {filteredUnread > 0 && (
                <Badge variant="secondary" className="ms-1.5 text-xs px-1.5 py-0 h-5">{filteredUnread}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="applications">
              {t('hire.notifications.applications')}
              {appCount > 0 && (
                <Badge variant="secondary" className="ms-1.5 text-xs px-1.5 py-0 h-5">{appCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="interviews">
              {t('hire.notifications.interviews')}
              {intCount > 0 && (
                <Badge variant="secondary" className="ms-1.5 text-xs px-1.5 py-0 h-5">{intCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="system">
              {t('hire.notifications.system')}
              {sysCount > 0 && (
                <Badge variant="secondary" className="ms-1.5 text-xs px-1.5 py-0 h-5">{sysCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4 space-y-2">
            <NotificationList notifications={filteredNotifications} onMarkRead={(id) => markRead(id)} language={i18n.language} viewLabel={t('common.view')} />
          </TabsContent>
          <TabsContent value="applications" className="mt-4 space-y-2">
            <NotificationList notifications={filterByCategory('applications')} onMarkRead={(id) => markRead(id)} language={i18n.language} viewLabel={t('common.view')} />
          </TabsContent>
          <TabsContent value="interviews" className="mt-4 space-y-2">
            <NotificationList notifications={filterByCategory('interviews')} onMarkRead={(id) => markRead(id)} language={i18n.language} viewLabel={t('common.view')} />
          </TabsContent>
          <TabsContent value="system" className="mt-4 space-y-2">
            <NotificationList notifications={filterByCategory('system')} onMarkRead={(id) => markRead(id)} language={i18n.language} viewLabel={t('common.view')} />
          </TabsContent>
        </Tabs>

        {!isLoading && filteredNotifications.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">{t('hire.notifications.empty')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}

function NotificationList({
  notifications,
  onMarkRead,
  language,
  viewLabel,
}: {
  notifications: EmployerNotification[];
  onMarkRead: (id: string) => void;
  language: string;
  viewLabel: string;
}) {
  return (
    <>
      {notifications.map(n => (
        <NotificationCard key={n.id} notification={n} onMarkRead={() => onMarkRead(n.id)} language={language} viewLabel={viewLabel} />
      ))}
    </>
  );
}

function NotificationCard({
  notification: n,
  onMarkRead,
  language,
  viewLabel,
}: {
  notification: EmployerNotification;
  onMarkRead: () => void;
  language: string;
  viewLabel: string;
}) {
  const navigate = useNavigate();
  const Icon = typeIcons[n.notification_type] || Bell;
  const isUnread = !n.read_at;
  const link = getNotificationLink(n);

  const handleClick = () => {
    if (isUnread) onMarkRead();
    if (link) navigate(link);
  };

  return (
    <Card
      className={`hover:shadow-sm transition-shadow cursor-pointer ${isUnread ? 'border-primary/30 bg-primary/[0.02]' : ''}`}
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isUnread ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-sm ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'}`}>
                {n.title}
              </p>
              {isUnread && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
            </div>
            {n.body && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
            )}
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-muted-foreground/60">
                {formatRelativeTime(n.created_at, language)}
              </p>
              {link && (
                <span className="text-xs text-primary flex items-center gap-0.5">
                  <ExternalLink className="w-3 h-3" />
                  {viewLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
